import { useMutation, useQuery } from '@apollo/client'
import { Tab, Typography } from '@mui/material'
import {
    CompanyRecruiterContactEntry,
    GetCompanyQuery,
} from '__generated__/graphql'
import { FC, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { UpdateCompanyRecruiterContacts } from 'recruiter/graphql/companies.gql'
import { ContactGroups } from 'recruiter/types/ContactGroups'
import DrawerComponent from 'shared/components/DrawerComponent'
import RecruitersAutocomplete from 'shared/components/RecruitersAutocomplete/RecruitersAutocomplete'
import { GetRecruitersByIds } from 'shared/components/RecruitersAutocomplete/RecruitersAutocomplete.gql'
import useToast from 'shared/components/Toast/useToast'
import { getPxAsRem } from 'shared/utils'
import { formatGraphQLError } from 'shared/utils/graphQL'
import { ContactSection, ContentWrapper, StyledTabs } from './styled'

interface CPIContactsDrawerProps {
    open: boolean
    onClose: () => void
    data: GetCompanyQuery
    refetch: () => void
}

const CPIContactsDrawer: FC<CPIContactsDrawerProps> = ({
    open,
    onClose,
    data,
    refetch,
}) => {
    const [updatedContacts, setUpdatedContacts] = useState<
        Map<string, CompanyRecruiterContactEntry>
    >(new Map())

    const toast = useToast()
    const { companyId } = useParams()
    const [selectedTab, setSelectedTab] =
        useState<keyof typeof ContactGroups>('Pre-MBA')
    const [updateCompanyRecruiterContacts] = useMutation(
        UpdateCompanyRecruiterContacts
    )

    const selectedRecruiterIds = Array.from(
        updatedContacts.values().map((contact) => contact.recruiterId)
    ).filter((val) => val !== undefined && val !== null)

    const { data: recruitersData } = useQuery(GetRecruitersByIds, {
        variables: {
            ids: selectedRecruiterIds,
        },
        skip: selectedRecruiterIds.length === 0,
    })

    useEffect(() => {
        if (data?.company.recruiterContacts) {
            const initialContacts = new Map<
                string,
                CompanyRecruiterContactEntry
            >()
            data.company.recruiterContacts.forEach((contact) => {
                const key = `${contact.group}-${contact.contactType}`
                initialContacts.set(key, {
                    recruiterId: contact.recruiter?.id ?? '',
                    group: contact.group,
                    contactType: contact.contactType,
                })
            })
            setUpdatedContacts(initialContacts)
        }
    }, [data])

    const handleTabChange = (_, newValue) => {
        setSelectedTab(newValue)
    }

    const getUpdatedRecruiterContacts = () => {
        return Array.from(updatedContacts.values())
    }

    const handlePrimaryContactChange = (newValue) => {
        const key = `${selectedTab}-Primary Contact`

        if (newValue) {
            updatedContacts.set(key, {
                recruiterId: newValue.id,
                contactType: 'Primary Contact',
                group: selectedTab,
            })
        } else {
            updatedContacts.delete(key)
        }

        setUpdatedContacts(new Map(updatedContacts))
    }

    const handleSecondaryContactChange = (newValue) => {
        const key = `${selectedTab}-Secondary Contact`

        if (newValue) {
            updatedContacts.set(key, {
                recruiterId: newValue.id,
                contactType: 'Secondary Contact',
                group: selectedTab,
            })
        } else {
            updatedContacts.delete(key)
        }

        setUpdatedContacts(new Map(updatedContacts))
    }

    const handleSave = () => {
        updateCompanyRecruiterContacts({
            variables: {
                companyId: companyId ?? '',
                recruiterContacts: getUpdatedRecruiterContacts(),
            },
            onCompleted: (data) => {
                if (data.updateCompanyRecruiterContacts.success) {
                    toast({
                        title: 'Success',
                        severity: 'success',
                        message: 'Recruiter contacts updated successfully',
                    })
                    refetch()
                    onClose()
                } else {
                    toast({
                        title: 'Error',
                        severity: 'error',
                        message:
                            data.updateCompanyRecruiterContacts.message ??
                            'Failed to update recruiter contacts. Please try again.',
                    })
                }
            },
            onError: (error) => {
                toast({
                    title: 'Error',
                    severity: 'error',
                    message: formatGraphQLError(
                        error,
                        'Failed to update recruiter contacts. Please try again.'
                    ),
                })
            },
        })
    }

    return (
        <DrawerComponent
            open={open}
            onClose={onClose}
            hasCancelButton
            onCancel={onClose}
            headerTitle="CPI Contacts"
            primaryActionText="Save"
            onPrimaryAction={handleSave}
        >
            <StyledTabs
                value={selectedTab}
                onChange={handleTabChange}
                indicatorColor="primary"
                textColor="primary"
                variant="fullWidth"
            >
                {Object.values(ContactGroups).map((group) => (
                    <Tab key={group} label={group} value={group} />
                ))}
            </StyledTabs>
            <ContentWrapper>
                <ContactSection>
                    <Typography variant="h5" sx={{ py: 1 }}>
                        Primary Contact
                    </Typography>
                    <RecruitersAutocomplete
                        fullWidth
                        getOptionLabel={(option) =>
                            `${option?.user?.nickname ?? option?.user?.firstName} ${option?.user?.lastName}`
                        }
                        value={
                            recruitersData?.recruitersById.find(
                                (recruiter) =>
                                    recruiter?.id ===
                                    updatedContacts.get(
                                        `${selectedTab}-Primary Contact`
                                    )?.recruiterId
                            ) || null
                        }
                        onChange={(event, newValue) =>
                            handlePrimaryContactChange(newValue)
                        }
                        label="Primary Recruiter Name"
                        sx={{ marginBottom: getPxAsRem(20) }}
                    />
                </ContactSection>
                <ContactSection>
                    <Typography variant="h5" sx={{ py: 1 }}>
                        Secondary Contact
                    </Typography>
                    <RecruitersAutocomplete
                        fullWidth
                        getOptionLabel={(option) =>
                            `${option?.user?.nickname ?? option?.user?.firstName} ${option?.user?.lastName}`
                        }
                        value={
                            recruitersData?.recruitersById.find(
                                (recruiter) =>
                                    recruiter?.id ===
                                    updatedContacts.get(
                                        `${selectedTab}-Secondary Contact`
                                    )?.recruiterId
                            ) || null
                        }
                        onChange={(event, newValue) =>
                            handleSecondaryContactChange(newValue)
                        }
                        label="Secondary Recruiter Name"
                        sx={{ marginBottom: getPxAsRem(20) }}
                    />
                </ContactSection>
            </ContentWrapper>
        </DrawerComponent>
    )
}

export default CPIContactsDrawer
